/**
 * /ticket — support ticket form and the account's ticket list.
 *
 * Transcribed from the monolith's `view === "ticket"` block.
 *
 * SIMULATED. Submitting appends a row to a client-side list and toasts that the
 * team will follow up by email. Nothing is transmitted, no email is sent, and the
 * list resets on reload. See src/stores/supportStore.ts and
 * docs/api-contract.md, "Support tickets".
 */
import { useEffect, useState } from 'react';
import { CheckCircle2, Clock, Send } from 'lucide-react';
import { Btn, LoadError, LoadSkeleton, SectionHeading } from '../components/ui';
import { Select, TextField } from '../components/forms';
import { UNKNOWN_API_ERROR_MESSAGE } from '../api/errors';
import { USE_MOCK_API } from '../config';
import { TICKET_CATEGORIES } from '../data/faq';
import { isFirstLoad } from '../stores/loadState';
import { useSupportStore } from '../stores/supportStore';
import { useAuthStore } from '../stores/authStore';
import { useUiStore } from '../stores/uiStore';
import { c } from '../styles/theme';
import type { TicketStatus } from '../types/domain';

/*
  The ticket form's controls are 1px tighter than the app's default field padding
  (8px vs 9px vertical) and its textarea is 13px rather than 12px. Overridden here
  rather than changed in the shared components, because the shared values are
  correct everywhere else.
*/
const FIELD = { padding: '8px 10px' } as const;
const TEXTAREA = { padding: '8px 10px', fontSize: 13 } as const;

/** Open tickets read as accent-coloured; anything else reads as resolved. */
function StatusPill({ status }: { status: TicketStatus }) {
  const open = status === 'Open';
  return (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 11,
        padding: '3px 9px',
        borderRadius: 999,
        flexShrink: 0,
        background: open ? c.accentSoft : c.successSoft,
        color: open ? c.accent : c.success,
      }}
    >
      {open ? <Clock size={11} /> : <CheckCircle2 size={11} />}
      {status}
    </span>
  );
}

export default function Ticket() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // One field per selector: Zustand v5 compares with Object.is, so an object
  // literal selector would re-render on every unrelated store change.
  const tickets = useSupportStore((s) => s.tickets);
  const subject = useSupportStore((s) => s.subject);
  const category = useSupportStore((s) => s.category);
  const message = useSupportStore((s) => s.message);
  const email = useSupportStore((s) => s.email);
  const setSubject = useSupportStore((s) => s.setSubject);
  const setCategory = useSupportStore((s) => s.setCategory);
  const setMessage = useSupportStore((s) => s.setMessage);
  const setEmail = useSupportStore((s) => s.setEmail);
  const submit = useSupportStore((s) => s.submit);
  const load = useSupportStore((s) => s.load);
  const loadTickets = useSupportStore((s) => s.loadTickets);
  const showToast = useUiStore((s) => s.showToast);

  /*
    THE TICKET READ'S ONE CALL SITE, and it is here rather than in hooks/useAppData for the
    reason that hook's header gives by name: "the Support Center is one screen rather than
    app-wide state — so it hydrates from its own route when there is a backend to hydrate
    from, not from the shell." This is that route.

    GATED ON `USE_MOCK_API`, the same condition and the same argument as useAppData. In mock
    mode `getTickets()` returns the seed list the store is already holding, so a fetch would
    buy nothing and would DISCARD any ticket submitted this session (supportStore.loadTickets
    says so in its own docstring) behind 350ms of latency.

    Once, on mount. Not on every render, and not per submission: `submit()` prepends the new
    ticket itself, and re-reading the list afterwards would replace a ticket the server has
    just accepted with a list that may not include it yet.
  */
  useEffect(() => {
    if (USE_MOCK_API || !isAuthenticated) return;
    void loadTickets();
  }, [isAuthenticated, loadTickets]);

  /**
   * True while a submission is in flight, so the button admits one at a time.
   *
   * `supportStore.submit` prepends a ticket for every resolution and `supportApi.createTicket`
   * resolves after MOCK_LATENCY_MS, so two presses inside that window filed TWO identical
   * tickets and toasted twice — and on a real backend that is two records for one problem,
   * which someone has to reconcile. The store has no `submitting` field and deliberately
   * should not gain one: this is a fact about this button, and PaymentPanel's `submitting`
   * /`paying` pair is the pattern already established for it here.
   *
   * Route-local rather than in supportStore for the same reason the store holds the draft
   * fields but not this: the draft is shared state a second mount should see; an in-flight
   * press is not.
   */
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit() {
    if (submitting) return;
    setSubmitting(true);
    try {
      const result = await submit(isAuthenticated);
      showToast(result.ok ? 'Ticket submitted — our team will follow up by email' : result.error);
      if (result.ok) setSubmitted(true);
    } finally {
      // `submit()` does not reject — it returns a discriminated result — but the release is
      // in a `finally` so a future throw cannot leave the form permanently unusable, which
      // is the failure mode a stuck flag produces on the screen people reach when
      // something else is already broken.
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div
        className="lyx-ticket-page"
        style={{ minHeight: 360, display: 'grid', placeItems: 'center' }}
      >
        <section
          aria-labelledby="ticket-success-title"
          style={{
            width: '100%',
            maxWidth: 520,
            boxSizing: 'border-box',
            textAlign: 'center',
            background: c.surface1,
            border: `1px solid ${c.border}`,
            borderRadius: 12,
            padding: '36px 28px',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 52,
              height: 52,
              display: 'inline-grid',
              placeItems: 'center',
              borderRadius: '50%',
              color: c.success,
              background: c.successSoft,
              marginBottom: 16,
            }}
          >
            <CheckCircle2 size={28} />
          </span>
          <h1 id="ticket-success-title" style={{ margin: '0 0 8px', fontSize: 22 }}>
            Ticket submitted
          </h1>
          <p style={{ margin: '0 auto 22px', maxWidth: 390, color: c.text2, fontSize: 13 }}>
            We received your support request. Our team will follow up by email, typically within a
            day.
          </p>
          <Btn type="button" onClick={() => setSubmitted(false)}>
            Open new ticket
          </Btn>
        </section>
      </div>
    );
  }

  return (
    <div className="lyx-ticket-page">
      <div className="lyx-ticket-fixed">
        <SectionHeading
          as="h1"
          title="Support Ticket"
          subtitle="Tell us what's going on — we typically reply within a day"
          marginBottom={16}
        />

        {/* A real <form>, so Enter in Category or Subject submits — the same fix Login.tsx and
            NewProjectForm.tsx carry, with the same note in both: "a real <form> so Enter
            submits, which the prototype's div did not". Enter inside the Message textarea still
            inserts a newline, which is correct and is why the textarea is not a reason to avoid
            the element. preventDefault because there is nowhere to post to; the handler is the
            submission. */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
          style={{
            background: c.surface1,
            border: `1px solid ${c.border}`,
            borderRadius: 12,
            padding: '16px 18px',
            marginBottom: 22,
          }}
        >
          <Select
            label="Category"
            value={category}
            onChange={setCategory}
            options={TICKET_CATEGORIES}
            marginBottom={12}
            style={FIELD}
          />

          {!isAuthenticated ? (
            <TextField
              label="Email"
              type="email"
              required
              value={email}
              onChange={setEmail}
              placeholder="you@example.com"
              marginBottom={12}
              style={FIELD}
            />
          ) : null}

          <TextField
            label="Subject"
            value={subject}
            onChange={setSubject}
            placeholder="Short summary of the issue"
            marginBottom={12}
            style={FIELD}
          />

          <TextField
            label="Message"
            value={message}
            onChange={setMessage}
            multiline
            rows={4}
            placeholder="Describe what happened, what you expected, and any error messages"
            marginBottom={14}
            style={TEXTAREA}
          />

          <Btn type="submit" disabled={submitting}>
            <Send size={13} />
            {submitting ? 'Submitting…' : 'Submit ticket'}
          </Btn>
        </form>
      </div>

      {isAuthenticated ? (
        <div className="lyx-ticket-list">
          <p style={{ fontSize: 12, color: c.text3, margin: '0 0 8px' }}>Your tickets</p>
          <div className="lyx-ticket-list-body">
            {/* The four read states. This list had NO empty state and no failure state at all — it
              was a bare `.map`, so a failed GET /support/tickets rendered a heading with nothing
              under it and a user with an open ticket would conclude it had never been received.
              The form above stays usable in every state: not being able to LIST your tickets is
              not a reason to stop you raising one. */}
            {isFirstLoad(load, tickets.length > 0) ? (
              <LoadSkeleton rows={2} rowHeight={44} label="Loading your tickets…" />
            ) : load.status === 'error' && tickets.length === 0 ? (
              <LoadError
                message={load.error?.message ?? UNKNOWN_API_ERROR_MESSAGE}
                onRetry={loadTickets}
              >
                <p style={{ fontSize: 12, color: c.text2, margin: '8px 0 0' }}>
                  Any ticket you have already sent is unaffected — this is the list that could not
                  be read. You can still submit a new one above.
                </p>
              </LoadError>
            ) : tickets.length === 0 ? (
              <p style={{ fontSize: 12, color: c.text2, margin: '4px 0 0' }}>
                You have no tickets yet. Anything you submit above will appear here.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {tickets.map((t) => (
                  <div
                    key={t.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 0',
                      borderBottom: `1px solid ${c.border}`,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontWeight: 500, fontSize: 13, margin: 0 }}>{t.subject}</p>
                      <p style={{ fontSize: 11, color: c.text3, margin: 0 }}>
                        {t.category} · {t.date}
                      </p>
                    </div>
                    <StatusPill status={t.status} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
